import { jsx as _jsx } from "react/jsx-runtime";
import { createDOMRenderer, FluentProvider, Portal, RendererProvider } from "@fluentui/react-components";
import { useCallback, useEffect, useImperativeHandle, useState } from "react";
import { ToastProvider } from "../primitives/toast";
import { OpenPopupWindow } from "./popupWindow";
/**
 * Allows displaying a child window that can contain child components.
 * @param props Props for the child window.
 * @returns The child window component.
 */
export const ChildWindow = (props) => {
    const { id, children, onOpenChange, imperativeRef: imperativeRef } = props;
    const [windowState, setWindowState] = useState();
    const [popupHandle, setPopupHandle] = useState();
    // This function is just for creating the child window itself. It is a function because
    // it must be called synchronously in response to a user interaction (e.g. button click),
    // otherwise the browser will block it as a scripted popup.
    const createWindow = useCallback((options = {}) => {
        const handle = OpenPopupWindow({
            id,
            title: options.title ?? id,
            defaultWidth: options.defaultWidth,
            defaultHeight: options.defaultHeight,
            defaultLeft: options.defaultLeft,
            defaultTop: options.defaultTop,
            onClose: () => {
                // Triggered when the popup is closed for any reason (user dismissal, parent unload,
                // or programmatic dispose). Clear the popup handle so the effect cleanup runs and
                // `onOpenChange(false)` is propagated up the tree (which lets a parent shell re-dock
                // a previously-undocked pane). teardown() is idempotent so calling dispose() again
                // from the effect cleanup is a safe no-op.
                setPopupHandle(undefined);
            },
        });
        if (handle) {
            setPopupHandle((current) => {
                // Close any existing child window before adopting the new one.
                current?.dispose();
                return handle;
            });
        }
    }, [id]);
    useImperativeHandle(imperativeRef, () => {
        return {
            open: createWindow,
            close: () => {
                setPopupHandle((current) => {
                    current?.dispose();
                    return undefined;
                });
            },
        };
    }, [createWindow]);
    // This side effect runs any time the popup handle changes. It does the rest of the child window
    // setup work, including creating resources and state needed to properly render the content of the child window.
    useEffect(() => {
        if (!popupHandle) {
            return undefined;
        }
        const popupDocument = popupHandle.popupWindow.document;
        // Use the popup's hostElement as the React mount point. OpenPopupWindow configures it as a
        // flex column that fills the popup body, so the FluentProvider (also a flex column with
        // flex-grow: 1) inside it can fill the available space.
        setWindowState({ mountNode: popupHandle.hostElement, renderer: createDOMRenderer(popupDocument) });
        onOpenChange?.(true);
        return () => {
            // Tear down the popup. The cached handle's dispose() handles bounds saving and
            // listener cleanup; React state is reset so the Portal/Provider tree unmounts.
            popupHandle.dispose();
            setWindowState(undefined);
            onOpenChange?.(false);
        };
    }, [popupHandle]);
    if (!windowState) {
        return null;
    }
    const { mountNode, renderer } = windowState;
    return (_jsx(Portal, { mountNode: mountNode, children: _jsx(RendererProvider, { renderer: renderer, targetDocument: mountNode.ownerDocument, children: _jsx(FluentProvider, { style: {
                    display: "flex",
                    flexGrow: 1,
                    flexDirection: "column",
                    overflow: "hidden",
                }, applyStylesToPortals: false, targetDocument: mountNode.ownerDocument, children: _jsx(ToastProvider, { children: children }) }) }) }));
};
//# sourceMappingURL=childWindow.js.map